import React, { createContext, useState, useCallback, ReactNode } from "react";

// Type definitions
export interface PageData {
  title: string;
  icon: React.ReactNode | null;
  breadcrumbs: string[];
  showBackButton: boolean;
  customActions: React.ReactNode | null;
}

// Allow partial updates for convenience
export type SetPageDataInput = Partial<PageData> | ((prev: PageData) => Partial<PageData> | PageData);

export interface PageDataContextValue {
  pageData: PageData;
  setPageData: (input: SetPageDataInput) => void;
}

interface PageDataProviderProps {
  children: ReactNode;
}

// Default page data values
const defaultPageData: PageData = {
  title: "",
  icon: null,
  breadcrumbs: [],
  showBackButton: false,
  customActions: null,
};

// Create context with proper typing
export const PageDataContext = createContext<PageDataContextValue | undefined>(undefined);

export const PageDataProvider: React.FC<PageDataProviderProps> = ({ children }) => {
  const [pageData, setPageDataState] = useState<PageData>(defaultPageData);

  // Custom setter that supports partial updates
  const setPageData = useCallback((input: SetPageDataInput) => {
    setPageDataState((prev) => {
      if (typeof input === "function") {
        const result = input(prev);
        return { ...prev, ...result };
      }
      return { ...prev, ...input };
    });
  }, []);

  return (
    <PageDataContext.Provider value={{ pageData, setPageData }}>
      {children}
    </PageDataContext.Provider>
  );
};
